<template>
  <div>
    <v-timeline v-if="getTimeline(ticketId) && getTimeline(ticketId).length > 0" dense>
      <v-slide-x-transition group>
        <v-timeline-item
          v-for="(c, index) in sortTimeLine"
          :key="index"
          fill-dot
          :color="getCommentTypes[c.type].color"
          small
        >
          <template #icon>
            <v-avatar v-if="agentsMap[c.uid] && agentsMap[c.uid].photo" size="24">
              <img :src="agentsMap[c.uid].photo" :alt="agentsMap[c.uid].name" />
            </v-avatar>
            <v-icon
              v-if="
                (c.type === $activityTypes.WHATSAPP && !c.uid && !c.activity_type && !c.from_me) ||
                c.type === $activityTypes.CONTACT
              "
              x-small
              color="white"
            >
              fas fa-user
            </v-icon>
          </template>
          <v-card class="px-4 py-2">
            <p class="mb-0">
              <strong v-if="agentsMap[c.uid]" class="pr-1" v-text="agentsMap[c.uid].full_name" />
              <strong v-if="agentsMap[c.uid]">·</strong>
              <strong v-if="c.created_at" v-text="$formatTimestamp(c.created_at, 'large')" />
            </p>

            <template v-if="c.wa_referral">
              <v-card
                outlined
                class="my-2 referral-card grey lighten-4"
                :href="c.wa_referral.source_url"
                target="_blank"
                rel="noopener noreferrer"
              >
                <v-img
                  v-if="c.wa_referral.media_type === 'image' && c.wa_referral.image_url"
                  :src="c.wa_referral.image_url"
                  max-height="180"
                  cover
                />
                <div
                  v-else-if="c.wa_referral.media_type === 'video'"
                  class="referral-video-placeholder"
                  style="position: relative"
                >
                  <v-img v-if="c.wa_referral.thumbnail_url" :src="c.wa_referral.thumbnail_url" max-height="180" cover />
                  <div v-else class="d-flex align-center justify-center grey lighten-3" style="height: 120px" />
                  <div
                    class="d-flex align-center justify-center"
                    style="position: absolute; inset: 0; background: rgba(0, 0, 0, 0.3)"
                  >
                    <v-icon large color="white">fas fa-play-circle</v-icon>
                  </div>
                </div>
                <v-card-text class="pa-2">
                  <div class="text-caption grey--text text-uppercase font-weight-bold mb-1">
                    <v-icon x-small color="grey">fas fa-ad</v-icon>
                    Anuncio
                  </div>
                  <span v-if="c.wa_referral.headline" class="font-weight-bold text--primary">
                    {{ c.wa_referral.headline }}
                  </span>
                </v-card-text>
              </v-card>
            </template>

            <template v-if="c.has_media_content && c.file_url">
              <img
                v-if="['image', 'sticker'].includes(c.wa_message_type)"
                :src="c.file_url"
                alt="Image"
                :style="{ width: c.wa_message_type === 'sticker' ? '50%' : '100%' }"
                class="pt-2"
              />
              <audio v-if="c.wa_message_type === 'audio'" :src="c.file_url" controls class="w100 pt-2">
                Tu navegador no soporta la reproducción de audio.
              </audio>
              <video v-if="c.wa_message_type === 'video'" controls width="100%" class="pt-2">
                <source :src="c.file_url" />
                Tu navegador no soporta la reproducción de video.
              </video>
              <v-btn
                v-if="c.wa_message_type === 'document'"
                :loading="loadingMedia && commentIdSelected === c.id"
                block
                text
                outlined
                color="primary"
                class="mt-1"
                @click="onDownloadCommentMedia(c)"
              >
                <v-icon x-small>fas fa-file-download</v-icon>
                <span class="ml-2 text-caption">Descargar documento</span>
              </v-btn>
            </template>

            <template
              v-if="!c.file_url && c.wa_message_type && ['image', 'document', 'sticker'].includes(c.wa_message_type)"
            >
              <img
                v-if="c.media_url"
                :src="c.media_url"
                alt="Image"
                :style="{ width: c.wa_message_type === 'sticker' ? '50%' : '100%' }"
              />
              <v-btn
                v-else-if="['image', 'sticker'].includes(c.wa_message_type)"
                :loading="loadingMedia && commentIdSelected === c.id"
                block
                text
                outlined
                color="primary"
                class="mt-1"
                @click="onViewCommentMedia(c)"
              >
                <v-icon x-small>
                  {{ c.wa_message_type === "image" ? "fas fa-image" : "far fa-sticky-note" }}
                </v-icon>
                <span class="ml-2 text-caption"> Ver {{ c.wa_message_type === "image" ? "imagen" : "sticker" }} </span>
              </v-btn>
              <v-btn
                v-else-if="c.wa_message_type === 'document'"
                :loading="loadingMedia && commentIdSelected === c.id"
                block
                text
                outlined
                color="primary"
                class="mt-1"
                @click="onDownloadCommentMedia(c)"
              >
                <v-icon x-small>fas fa-file-download</v-icon>
                <span class="ml-2 text-caption">Descargar documento</span>
              </v-btn>
            </template>

            <div class="text-caption pb-2" v-html="c.text_formatted || c.text" />

            <template v-if="c.wa_location">
              <a
                :href="`https://www.google.com/maps/search/?api=1&query=${c.wa_location.latitude},${c.wa_location.longitude}`"
                class="text-caption"
                target="_blank"
              >
                Ver en Google Maps
              </a>
              <br />
              <a
                :href="`http://maps.apple.com/?ll=${c.wa_location.latitude},${c.wa_location.longitude}`"
                class="text-caption"
                target="_blank"
              >
                Ver en Apple Maps
              </a>
            </template>

            <div v-if="c.order_id" class="mb-1">
              <v-col v-if="c.products" cols="12" class="pr-5">
                <h6 class="text-caption mb-2">Productos relacionados:</h6>
                <v-simple-table class="mb-4" style="overflow-x: auto; border: 1px solid lightgrey">
                  <thead class="grey lighten-4">
                    <tr>
                      <th class="text-center">Nombre</th>
                      <th class="text-center">Cantidad</th>
                      <th class="text-center">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody class="text-center">
                    <tr v-for="(product, i) in c.products" :key="i">
                      <td>{{ product.name || product.id }}</td>
                      <td>{{ product.quantity }}</td>
                      <td>{{ $formatCurrency(product.price * product.quantity) }}</td>
                    </tr>
                    <tr>
                      <td colspan=" 2" class="text-right subtitle">Total</td>
                      <td class="grey lighten-5">
                        <b class="primary--text subtitle">{{ $formatCurrency(c.total_products) }}</b>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
              <v-btn
                v-if="![$roles.ADVISOR, $roles.SUPERVISOR].includes(getAgent.role)"
                text
                small
                :to="{ name: 'order_detail', params: { id: c.order_id } }"
                target="_blank"
                color="primary"
              >
                <v-icon x-small class="pr-2"> fas fa-external-link-alt </v-icon> Ver detalle de la orden
              </v-btn>
            </div>

            <div class="d-flex justify-space-between">
              <v-chip :color="getCommentTypes[c.type].color" rounded outlined small class="my-1 mr-1">
                <v-icon left size="12">
                  {{ getCommentTypes[c.type].icon }}
                </v-icon>

                <span v-if="c.type == $activityTypes.CALL">Llamada</span>
                <span v-else-if="c.type == $activityTypes.COMMENT_BY_AGENT">Comentario del agente</span>
                <span v-else-if="c.type == $activityTypes.EVENT">
                  {{ `${c.event_date} ${c.event_time} ${c.event_time_end ? ` - ${c.event_time_end}` : ""}` }}
                </span>
                <span v-else-if="c.type == $activityTypes.WHATSAPP">WhatsApp</span>
                <span v-else-if="c.type == $activityTypes.FOLLOW_UP">Seguimiento</span>
                <span v-else>Comentario</span>
              </v-chip>

              <div>
                <v-btn
                  v-if="!c.from_me && c.type == $activityTypes.WHATSAPP"
                  :loading="loadingMedia && commentIdSelected === c.id"
                  icon
                  small
                  @click="onOpenAssistantLogModal(c.text)"
                >
                  <v-icon x-small>fas fa-clipboard-list</v-icon>
                </v-btn>
                <v-btn
                  v-if="c.media_url"
                  :loading="loadingMedia && commentIdSelected === c.id"
                  icon
                  small
                  @click="onDownloadCommentMediaImage(c)"
                >
                  <v-icon x-small>fas fa-download</v-icon>
                </v-btn>
                <v-btn
                  v-if="c.message_template_id && messageTemplatesMap[c.message_template_id]"
                  icon
                  small
                  @click="onOpenMessageTemplatePreviewModal(messageTemplatesMap[c.message_template_id])"
                >
                  <v-icon x-small>fas fa-eye</v-icon>
                </v-btn>
                <v-btn
                  v-else-if="c.formatted_message"
                  icon
                  small
                  @click="onOpenMessagePreviewModal(c.formatted_message)"
                >
                  <v-icon x-small>fas fa-eye</v-icon>
                </v-btn>
                <v-btn v-else-if="c.reply_message" icon small @click="onOpenReplyMessagePreviewModal(c.reply_message)">
                  <v-icon x-small>fas fa-eye</v-icon>
                </v-btn>
                <template v-if="c.wa_id">
                  <v-icon v-if="c.status === 'read'" x-small color="blue">fas fa-check-double</v-icon>
                  <v-icon v-else-if="c.status === 'delivered'" x-small>fas fa-check-double</v-icon>
                  <v-icon v-else-if="c.status === 'failed'" x-small color="red"> fas fa-times </v-icon>
                  <v-icon v-else x-small>fas fa-check</v-icon>
                </template>
              </div>
            </div>

            <v-alert
              v-if="c.wa_id && c.status === 'failed'"
              :icon="false"
              text
              dense
              type="error"
              class="mb-0 mt-2 text-caption"
            >
              {{ c.error ? c.error.message : "Ocurrió un problema al enviar el mensaje" }}
            </v-alert>

            <div v-if="c.type === $activityTypes.EVENT && c.uid === getAgent.uid" class="text-right pa-2">
              <BtnCalendar
                :event-id.sync="c.id"
                :date.sync="c.event_date"
                :hour-start.sync="c.event_time"
                :hour-end.sync="c.event_time_end"
                :title.sync="c.event_name"
                :detail.sync="c.detail"
              />
            </div>
          </v-card>
        </v-timeline-item>
      </v-slide-x-transition>
    </v-timeline>
    <div v-else class="text-center py-4">Aún no hay comentarios</div>

    <MessageTemplatePreviewModal
      v-if="dialogWhatsappMessageTemplatePreview"
      :dialog.sync="dialogWhatsappMessageTemplatePreview"
      :message-template-selected="whatsappMessageTemplate"
    />
    <ReplyMessagePreviewModal
      v-if="dialogWhatsappReplyMessagePreview"
      :dialog.sync="dialogWhatsappReplyMessagePreview"
      :reply-message-selected="whatsappReplyMessage"
    />
    <MessagePreviewModal
      v-if="dialogWhatsappMessagePreview"
      :dialog.sync="dialogWhatsappMessagePreview"
      :message="whatsappMessage"
    />
    <AssistantLogModal
      v-if="dialogAssistantLog"
      :dialog.sync="dialogAssistantLog"
      :ticket-id.sync="ticketId"
      :input.sync="input"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import BtnCalendar from "@/components/buttons/BtnCalendar";
import ReplyMessagePreviewModal from "../whatsapp/ReplyMessagePreviewModal";
import MessagePreviewModal from "../whatsapp/MessagePreviewModal";
import MessageTemplatePreviewModal from "@/views/channels/whatsapp/message-templates/modals/Preview";
import AssistantLogModal from "@/components/tickets/AssistantLogModal";

export default {
  components: {
    BtnCalendar,
    ReplyMessagePreviewModal,
    MessagePreviewModal,
    MessageTemplatePreviewModal,
    AssistantLogModal,
  },
  props: {
    ticketId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      commentIdSelected: null,
      loadingMedia: false,
      whatsappReplyMessage: null,
      dialogWhatsappReplyMessagePreview: false,
      whatsappMessage: null,
      dialogWhatsappMessagePreview: false,
      whatsappMessageTemplate: null,
      dialogWhatsappMessageTemplatePreview: false,
      dialogAssistantLog: false,
      input: "",
      loadingMessageTemplates: false,
    };
  },
  computed: {
    ...mapGetters([
      "getAgent",
      "getAllAgentsMap",
      "getTimeline",
      "getSelectedTicketId",
      "getCommentTypes",
      "getWhatsAppMessageTemplatesMap",
    ]),
    sortTimeLine() {
      return this.getTimeline(this.ticketId);
    },
    agentsMap() {
      return this.getAllAgentsMap || {};
    },
    messageTemplatesMap() {
      return this.getWhatsAppMessageTemplatesMap || {};
    },
  },
  watch: {
    sortTimeLine: {
      immediate: true,
      handler(activities) {
        this.loadMessageTemplatesIfNeeded(activities);
      },
    },
  },
  async beforeMount() {
    if (this.ticketId) await this.getTicketTimeline({ ticket_id: this.ticketId });
  },
  methods: {
    ...mapActions([
      "getTicketTimeline",
      "getTicketTimelineCommentMedia",
      "updateTicketTimelineActivity",
      "getAllWhatsAppMessageTemplates",
    ]),
    async loadMessageTemplatesIfNeeded(activities) {
      if (
        this.loadingMessageTemplates ||
        this.getWhatsAppMessageTemplatesMap ||
        !(activities || []).some((activity) => activity.message_template_id)
      )
        return;

      try {
        this.loadingMessageTemplates = true;
        await this.getAllWhatsAppMessageTemplates();
      } catch (error) {
        console.error("timeline/getAllWhatsAppMessageTemplates: ", error);
      } finally {
        this.loadingMessageTemplates = false;
      }
    },
    onOpenAssistantLogModal(input) {
      this.input = input;
      this.dialogAssistantLog = true;
    },
    async onViewCommentMedia(comment) {
      try {
        this.loadingMedia = true;
        this.commentIdSelected = comment.id;

        let mediaUrl = comment.wa_media_url;

        if (!comment.from_me || comment.wa_media_uploaded) {
          const response = await this.getTicketTimelineCommentMedia({
            id: this.ticketId,
            commentId: comment.id,
          });

          mediaUrl = window.URL.createObjectURL(response.data);
        }

        this.updateTicketTimelineActivity({
          id: this.ticketId,
          activityId: comment.id,
          data: { media_url: mediaUrl },
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingMedia = false;
        this.commentIdSelected = null;
      }
    },
    async onDownloadCommentMedia(comment) {
      try {
        this.loadingMedia = true;
        this.commentIdSelected = comment.id;

        let mediaUrl = comment.wa_media_url || comment.file_url;

        if (!comment.from_me || comment.wa_media_uploaded) {
          const response = await this.getTicketTimelineCommentMedia({
            id: this.ticketId,
            commentId: comment.id,
          });

          mediaUrl = window.URL.createObjectURL(new Blob([response.data]));
        }

        let link = document.createElement("a");
        link.href = mediaUrl;
        link.setAttribute("download", comment.wa_document_filename);
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingMedia = false;
        this.commentIdSelected = null;
      }
    },
    async onDownloadCommentMediaImage(comment) {
      try {
        this.loadingMedia = true;
        this.commentIdSelected = comment.id;

        let extension;
        if (comment.from_me) {
          let linkComplete = comment.media_url.split("?");
          let link = linkComplete[linkComplete.length - 2];
          extension = link.split(".").pop();
        } else {
          extension = comment.wa_media_mime_type.split("/")[1];
        }

        let link = document.createElement("a");
        link.href = comment.media_url;
        link.setAttribute("download", `${comment.wa_media_name || Date.now()}.${extension}`);
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingMedia = false;
        this.commentIdSelected = null;
      }
    },
    onOpenReplyMessagePreviewModal(replyMessage) {
      this.whatsappReplyMessage = replyMessage;
      this.dialogWhatsappReplyMessagePreview = true;
    },
    onOpenMessagePreviewModal(message) {
      this.whatsappMessage = message;
      this.dialogWhatsappMessagePreview = true;
    },
    onOpenMessageTemplatePreviewModal(messageTemplate) {
      this.whatsappMessageTemplate = messageTemplate;
      this.dialogWhatsappMessageTemplatePreview = true;
    },
  },
};
</script>
